import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import i18n from '@/config/i18n.config'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { ButtonTypeEnum, ErrorDisplayTypeEnum } from '@/modules/core/models/enums'

import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import ServiceSchemaError from '@/modules/core/models/errors/ServiceSchemaError'

import MiddlewareService from '@/modules/core/services/MiddlewareService'
import DXPService from '@/modules/core/services/DXPService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'

import PetsConfigurations from '@/modules/pets/common/models/APP/PetsConfigurations'
import PetsInsurance from '@/modules/pets/common/models/APP/PetsInsurance'
import PetsRaterAnswers from '@/modules/pets/rater/models/APP/PetsRaterAnswers'
import PetsCommonMapper from '@/modules/pets/common/mappers'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import { RetrieveCoveragesCompleteRequest } from '@/modules/common/common/models/MID/requests/RetrieveCoveragesCompleteRequest'
import { RetrieveCoveragesCompleteResponse } from '@/modules/common/common/models/MID/responses/RetrieveCoveragesCompleteResponse'
import RaterPriceResponse from '@/modules/common/common/models/MID/responses/RaterPriceResponse'
import { CURRENCY } from '@/modules/common/common/constants/CommonConstants'
import GTMEventType from '@/modules/core/models/GTMEventType'
import { useGTMStore } from '@/modules/core/stores/gtmStore'
import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { priceFormat } from '@/utils/textUtils'

// singleton items
const coverages = ref<RetrieveCoveragesCompleteResponse>()
const globalConfig = ref(new PetsConfigurations())

export const usePetsStore = defineStore('raterPets', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const router = useRouter()
  const { pushPreservingMCode } = useNavigation()
  const commonStore = useCommonStore()
  const { t } = i18n.global
  const supportModalStore = useSupportModalStore()
  const gmtStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()

  const raterAnswers = ref(new PetsRaterAnswers())
  const insuranceProposals = ref<PetsInsurance[]>([])
  const insuranceProposalsSelected = ref<PetsInsurance[]>([])

  const guaranteesItems = computed(
    () =>
      coverages.value?.coverageInfoData
        ?.filter((cid) => cid.coverageInfoCapitals[0].sumInsured === '')
        ?.map((cid) => ({
          text: cid.description,
          coverageCode: cid.coverageCode,
          sumInsured: cid.coverageInfoCapitals[0].sumInsured
        }))
  )

  const additionalGuaranteesItems = computed(
    () =>
      coverages.value?.coverageInfoData
        ?.filter((cid) => cid.coverageInfoCapitals[0].sumInsured !== '')
        ?.map((cid) => {
          let capital = cid.coverageInfoCapitals
            .map((cic) => priceFormat(cic.sumInsured, 0))
            .join(', ')
          capital = capital.replace(new RegExp(', (?!.*, )', 'i'), ' y ')
          return {
            text: cid.description,
            description: `${t('pets.capital')} ${capital}${CURRENCY}`,
            coverageCode: cid.coverageCode,
            sumInsured: cid.coverageInfoCapitals[0].sumInsured,
            range:
              cid.coverageInfoCapitals.length > 1
                ? cid.coverageInfoCapitals.map((cic) => Number(cic.sumInsured))
                : undefined
          }
        })
  )

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function clearRaterAnswers() {
    raterAnswers.value = new PetsRaterAnswers()
  }

  function getRateFormAnswers() {
    return raterAnswers.value as PetsRaterAnswers
  }

  function setRateFormAnswers(answers: Partial<PetsRaterAnswers>) {
    if (!answers) return

    raterAnswers.value.updateAttributes(answers)
  }

  async function loadConfiguration() {
    try {
      const endpoint = ENDPOINTS.DXP_GET_BRANCH_PETS
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setModalDescription('copy_error_service_description')
          .setButtons([
            new ModalButton({
              text: 'header.help-text',
              icon: 'icon-message-text-01',
              type: ButtonTypeEnum.OUTLINE,
              onclick: () => {
                gmtStore.sendEvent({
                  event: GTMEventType.CALLMEBACK_BTN,
                  click_name: 'support-modal-btn',
                  click_id: 'support-modal-btn',
                  btn_label: t('header.help-text'),
                  value: sharedGtmDataStore.policyValue
                })
                supportModalStore.setNavTo('exit')
                supportModalStore.setOpenModal(true)
              }
            }),
            new ModalButton({
              text: 'copy_button_return',
              navTo: 'exit',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      globalConfig.value = new PetsConfigurations(data)
      raterAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function sendRate(
    options: {
      preventRouting?: boolean
      answers?: PetsRaterAnswers | undefined
      externalErrorHandling?: boolean
      checkLocation?: string
    } = {}
  ) {
    options.preventRouting = Boolean(options.preventRouting)
    options.externalErrorHandling = Boolean(options.externalErrorHandling)
    if (!options.checkLocation) {
      options.checkLocation = location.pathname
    }

    try {
      loadingStore.startLoading(
        'common.loading.processing',
        'common.loading.looking-for-best-products'
      )
      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_PETS
      const answers = options.answers ?? (raterAnswers.value as PetsRaterAnswers)
      const request = PetsCommonMapper.GetPricingMapper.toRequest(
        answers,
        true,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      if (options.answers) {
        raterAnswers.value.updateAttributes(options.answers)
      }

      const response = new RaterPriceResponse(data)
      if (response.listPricing.length === 0) {
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons([
            new ModalButton({
              text: 'components.InsuredSubheaderComponent.button'
            }),
            new ModalButton({
              text: 'copy_button_init_return',
              navTo: 'rater-nav',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      setRateFormAnswers({
        quotationId: response.quotationId,
        businessLine: response.listPricing[0].businessLine
      })
      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      insuranceProposals.value = _loadCompleteSheetProducts(dxpData, data.listPricing)

      if (insuranceProposals.value.length === 0) {
        const modalButtons = [
          new ModalButton({
            text: 'components.InsuredSubheaderComponent.button'
          }),
          new ModalButton({
            text: 'copy_button_init_return',
            navTo: 'rater-nav',
            type: ButtonTypeEnum.OUTLINE
          })
        ]
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons(modalButtons)
      }

      if (!options.preventRouting && options.checkLocation === location.pathname) {
        // check pathname to prevent to push a route when user clicks to go to back or other stuff
        await pushPreservingMCode(router, { name: `pets-insurance-proposals` })
      }
    } catch (error) {
      if (options.externalErrorHandling) {
        throw error
      }

      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  //** PRIVATE SUPPORT METHODS */
  // todo - COMMON composable or pets/common composable?
  async function _fetchDXPInsurances(products: ProductId[]) {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = PetsCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  // todo - COMMON composable or pets/common composable?
  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): PetsInsurance[] {
    const products: PetsInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = PetsCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance
  ) {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')

      const { error, data } = await _saveQuotation(quotationId, startDate, billingCycle, product)
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function loadSpecificProduct(productCode: string, clientCode: string) {
    try {
      const dxpData = await _fetchDXPInsurances([{ productCode, clientCode }])
      const DXPProduct = dxpData[0]
      const insurance = PetsCommonMapper.GetProductsMapper.fromResponse(DXPProduct, {})

      raterAnswers.value = new PetsRaterAnswers({
        product: insurance,
        businessLine: globalConfig.value.businessLineId
      })
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function _saveQuotation(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance
  ) {
    if (!quotationId) throw new Error('Quotation id is required')

    const endpoint = ENDPOINTS.SAVE_QUOTATION_PETS
    const request = PetsCommonMapper.SavePetsQuotationMapper.toRequest(
      quotationId,
      startDate,
      billingCycle,
      product,
      raterAnswers.value.pets.map((pet, i) => ({ name: pet.name, riskNumber: String(i + 1) }))
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    return { error, data }
  }

  async function sendBudget(
    quotationNumber: string,
    email: string,
    privacyPolicyConsent: boolean,
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance
  ) {
    try {
      loadingStore.startLoading('common.loading.processing')
      const quotationResult = await _saveQuotation(quotationId, startDate, billingCycle, product)
      if (quotationResult.error) {
        throw new ServiceError(quotationResult.data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle('copy_budget-service_error')
      }
      const endpoint = ENDPOINTS.SEND_BUDGET
      const request = { quotationNumber, to: email, privacyPolicyConsent }
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle('copy_budget-service_error')
      }

      toastStore.successToast('copy_budget-service_success')
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function getCoverages(preventCache = false) {
    try {
      if (coverages.value && !preventCache) {
        return coverages.value
      }
      loadingStore.startLoading('common.loading.processing')
      const endpoint = ENDPOINTS.GET_COVERAGES_COMPLETE

      const request = new RetrieveCoveragesCompleteRequest({
        businessLine: globalConfig.value.businessLineId
      })
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle('copy_rater_error_retrieving_coverages')
      }
      coverages.value = new RetrieveCoveragesCompleteResponse(data)
      return coverages.value
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  function getInsuranceProposals() {
    return insuranceProposals.value
  }

  function getMostRelevantProducts(insurances: PetsInsurance[]) {
    const sortedItems = insurances.sort((a, b) => a.order - b.order)

    return sortedItems.slice(0, 2)
  }

  return {
    getBranchConfiguration,
    loadConfiguration,
    getInsuranceProposals,
    getMostRelevantProducts,

    //* RATER
    clearRaterAnswers,
    sendBudget,
    hireProduct,
    //* RATER -> NEW
    insuranceProposals,
    insuranceProposalsSelected,
    getRateFormAnswers,
    setRateFormAnswers,
    sendRate,
    //* RATER -> SPECIFIC PRODUCT
    loadSpecificProduct,
    getCoverages,
    guaranteesItems,
    additionalGuaranteesItems
  }
})
